import { useState, useMemo, useRef, useEffect } from "react";
import { Check, ChevronDown, ChevronRight, Search } from "lucide-react";
import { Currency, Chain } from "@paywithglide/glide-js";
import { motion, AnimatePresence } from "framer-motion";
import { animations } from "../lib/animations";
import { LogoWithBadge } from "./LogoWithBadge";
import { selectBestCurrencyRepresentative } from "../lib/selectBestCurrencyRepresentative";

// Normalize text for search — maps Unicode lookalikes to their ASCII equivalents.
// Applied to both the query and the symbol so searches like "USDT" find "USD₮0".
function normalizeForSearch(text: string): string {
  return (
    text
      .toLowerCase()
      // Superscript digits → regular digits (e.g. ⁰ → 0)
      .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, (c) =>
        String("0123456789"["⁰¹²³⁴⁵⁶⁷⁸⁹".indexOf(c)]),
      )
      .replace(/[₀₁₂₃₄₅₆₇₈₉]/g, (c) =>
        String("0123456789"["₀₁₂₃₄₅₆₇₈₉".indexOf(c)]),
      )
      // Crypto currency symbols → their closest ASCII letter
      // so e.g. USD₮0 is searchable as "USDT" or "USDT0"
      .replace(/₮/g, "t")
      .replace(/₿/g, "b")
  );
}

type Props = {
  currencies: Currency[];
  chains: Chain[];
  selectedCurrencyId: string;
  selectedChainId: string;
  onSelect: (currencyId: string, chainId: string) => void;
  disabled?: boolean;
  className?: string;
};

export function CombinedTokenChainSelector({
  currencies,
  chains,
  selectedCurrencyId,
  selectedChainId,
  onSelect,
  disabled = false,
  className = "",
}: Props) {
  const [dropdownView, setDropdownView] = useState<"tokens" | "chains">(
    "tokens",
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Optimistic selection — updated immediately on click so logo swaps before prop propagates
  const [optimisticCurrencyId, setOptimisticCurrencyId] = useState<
    string | null
  >(null);
  const [optimisticChainId, setOptimisticChainId] = useState<string | null>(
    null,
  );
  const [maxHeight, setMaxHeight] = useState(260);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Clear optimistic selection once the parent prop has caught up
  useEffect(() => {
    if (
      optimisticCurrencyId &&
      optimisticChainId &&
      selectedCurrencyId === optimisticCurrencyId &&
      selectedChainId === optimisticChainId
    ) {
      setOptimisticCurrencyId(null);
      setOptimisticChainId(null);
    }
  }, [
    selectedCurrencyId,
    selectedChainId,
    optimisticCurrencyId,
    optimisticChainId,
  ]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isDropdownOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
        setDropdownView("tokens");
        setSelectedTokenSymbol("");
        setSearchQuery("");
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Group currencies by symbol for two-step selection
  const groupedCurrencies = useMemo(() => {
    const groups = new Map<string, Currency[]>();
    currencies.forEach((currency) => {
      const existing = groups.get(currency.symbol) || [];
      groups.set(currency.symbol, [...existing, currency]);
    });
    return Array.from(groups.entries()).map(([symbol, currencies]) => ({
      symbol,
      currencies,
      count: currencies.length,
    }));
  }, [currencies]);

  // Filter grouped currencies by search query (normalized to handle Unicode variants)
  const filteredCurrencies = useMemo(() => {
    if (!searchQuery.trim()) return groupedCurrencies;
    const q = normalizeForSearch(searchQuery);
    return groupedCurrencies.filter((g) =>
      normalizeForSearch(g.symbol).includes(q),
    );
  }, [groupedCurrencies, searchQuery]);

  // Calculate dropdown height and scroll to selected when it opens or view changes
  useEffect(() => {
    if (isDropdownOpen && triggerRef.current) {
      const buttonRect = triggerRef.current.getBoundingClientRect();

      let containerBottom = window.innerHeight;
      let parent = triggerRef.current.parentElement;

      while (parent && parent.tagName !== "BODY") {
        const style = window.getComputedStyle(parent);

        if (
          style.overflowY === "auto" ||
          style.overflowY === "scroll" ||
          style.overflowY === "hidden" ||
          (style.maxHeight && style.maxHeight !== "none")
        ) {
          containerBottom = parent.getBoundingClientRect().bottom;
          break;
        }

        parent = parent.parentElement;
      }

      const availableSpace = containerBottom - buttonRect.bottom - 40;
      const idealHeight = 260;
      setMaxHeight(Math.min(idealHeight, Math.max(availableSpace, 144)));

      if (listRef.current) {
        setTimeout(() => {
          if (listRef.current) {
            let element: Element | null = null;

            if (dropdownView === "tokens") {
              const selectedGroup = groupedCurrencies.find((group) =>
                group.currencies.some((c) => c.id === selectedCurrencyId),
              );
              if (selectedGroup) {
                const index = groupedCurrencies.findIndex(
                  (g) => g.symbol === selectedGroup.symbol,
                );
                element = listRef.current.querySelector(
                  `[data-token-index="${index}"]`,
                );
              }
            } else if (dropdownView === "chains") {
              element = listRef.current.querySelector(
                `[data-chain-id="${selectedChainId}"]`,
              );
            }

            if (element) {
              element.scrollIntoView({ block: "nearest", behavior: "auto" });
            }
          }
        }, 0);
      }
    }
  }, [
    isDropdownOpen,
    dropdownView,
    groupedCurrencies,
    selectedCurrencyId,
    selectedChainId,
  ]);

  const selectedCurrency = currencies.find(
    (c) => c.id === (optimisticCurrencyId ?? selectedCurrencyId),
  );
  const selectedChain = chains.find(
    (c) => c.id === (optimisticChainId ?? selectedChainId),
  );

  // When the user has clicked a token but hasn't picked a chain yet,
  // preview that token's logo in the trigger header immediately
  const previewTokenGroup = selectedTokenSymbol
    ? groupedCurrencies.find((g) => g.symbol === selectedTokenSymbol)
    : null;
  const previewCurrency = previewTokenGroup
    ? selectBestCurrencyRepresentative(previewTokenGroup.currencies)
    : null;

  const handleTokenSelect = (symbol: string) => {
    const selectedGroup = groupedCurrencies.find((g) => g.symbol === symbol);
    if (!selectedGroup) return;

    const supportedChains: Array<{
      chain: Chain;
      currency: Currency;
    }> = [];

    for (const chain of chains) {
      const supportedCurrency = selectedGroup.currencies.find((currency) => {
        try {
          const caip19 = currency.on(chain);
          return caip19 !== null && caip19 !== undefined;
        } catch {
          return false;
        }
      });

      if (supportedCurrency) {
        supportedChains.push({
          chain,
          currency: supportedCurrency,
        });
      }
    }

    if (supportedChains.length === 1) {
      const { chain, currency } = supportedChains[0];
      handleChainSelect(currency.id, chain.id);
    } else {
      setSelectedTokenSymbol(symbol);
      setSearchQuery(""); // reset so user can search chains
      setDropdownView("chains");
    }
  };

  const handleChainSelect = (currencyId: string, chainId: string) => {
    setOptimisticCurrencyId(currencyId);
    setOptimisticChainId(chainId);
    onSelect(currencyId, chainId);
    setIsDropdownOpen(false);
    setDropdownView("tokens");
    setSelectedTokenSymbol("");
    setSearchQuery("");
  };

  // Placeholder shown in the search input
  const searchPlaceholder =
    dropdownView === "chains"
      ? `Search chains for ${selectedTokenSymbol}...`
      : selectedCurrency && selectedChain
        ? `${selectedCurrency.symbol} on ${selectedChain.name}`
        : "Search tokens...";

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger — div instead of button so input can live inside */}
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (disabled) return;
          if (!isDropdownOpen) {
            setDropdownView("tokens");
            setSelectedTokenSymbol("");
            setSearchQuery("");
          }
          setIsDropdownOpen(!isDropdownOpen);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (!disabled) setIsDropdownOpen(!isDropdownOpen);
          }
        }}
        className={`
          w-full rounded-xl px-3 py-2.5 transition-all
          bg-[--color-background-secondary] border border-[--color-modal-border]
          flex items-center justify-between gap-2 outline-none
          ${disabled ? "opacity-50 cursor-not-allowed" : !isDropdownOpen ? "hover:bg-[--color-alert-background] cursor-pointer" : "cursor-text"}
        `}
      >
        {isDropdownOpen ? (
          /* Search mode — same space as the display */
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {previewCurrency ? (
              <img
                src={previewCurrency.logoUrl}
                alt={previewCurrency.symbol}
                className="w-6 h-6 flex-shrink-0 rounded-full"
              />
            ) : selectedCurrency && selectedChain ? (
              <LogoWithBadge
                logoUrl={selectedCurrency.logoUrl}
                badgeUrl={selectedChain.logoUrl}
                size="sm"
              />
            ) : (
              <Search className="w-4 h-4 flex-shrink-0 text-[--color-text-secondary]" />
            )}
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="bg-transparent outline-none flex-1 min-w-0 text-sm text-[--color-text-primary] placeholder:text-[--color-text-secondary]"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setIsDropdownOpen(false);
                  setSearchQuery("");
                }
              }}
            />
          </div>
        ) : (
          /* Display mode */
          <div className="flex items-center gap-2 min-w-0">
            {selectedCurrency && selectedChain ? (
              <>
                <LogoWithBadge
                  logoUrl={selectedCurrency.logoUrl}
                  badgeUrl={selectedChain.logoUrl}
                  size="sm"
                />
                <span className="font-medium text-sm text-[--color-text-primary] truncate">
                  {selectedCurrency.symbol} on {selectedChain.name}
                </span>
              </>
            ) : (
              <span className="text-sm text-[--color-text-secondary]">
                Select token and chain...
              </span>
            )}
          </div>
        )}
        <ChevronDown
          className={`w-4 h-4 text-[--color-text-primary] flex-shrink-0 transition-transform duration-75 ${isDropdownOpen ? "rotate-180" : ""}`}
        />
      </div>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={animations.slideDown.initial}
            animate={animations.slideDown.animate}
            exit={animations.slideDown.exit}
            transition={{
              type: "tween",
              ease: [0.25, 0.46, 0.45, 0.94],
              duration: 0.18,
            }}
            className="absolute top-full left-0 right-0 mt-2 z-[100]"
          >
            <div className="bg-[--color-background-secondary] border border-[--color-modal-border] rounded-xl shadow-lg overflow-hidden">
              <div
                ref={listRef}
                className="overflow-y-auto"
                style={{ maxHeight: `${maxHeight}px` }}
              >
                <AnimatePresence mode="wait" initial={false}>
                  {dropdownView === "tokens" ? (
                    /* Token List View */
                    <motion.div
                      key="tokens"
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{
                        type: "tween",
                        ease: [0.25, 0.46, 0.45, 0.94],
                        duration: 0.12,
                      }}
                      className="p-2 space-y-3"
                    >
                      <div>
                        <div className="px-2 pb-2 text-xs font-semibold text-[--color-text-secondary] tracking-wide">
                          All Tokens
                        </div>
                        {filteredCurrencies.length === 0 ? (
                          <div className="px-2 py-4 text-sm text-center text-[--color-text-secondary]">
                            No tokens match "{searchQuery}"
                          </div>
                        ) : (
                          filteredCurrencies.map((group, index) => {
                            const firstCurrency =
                              selectBestCurrencyRepresentative(
                                group.currencies,
                              );

                            const availableChains: Chain[] = [];
                            for (const chain of chains) {
                              const isSupported = group.currencies.some(
                                (currency) => {
                                  try {
                                    const caip19 = currency.on(chain);
                                    return (
                                      caip19 !== null && caip19 !== undefined
                                    );
                                  } catch {
                                    return false;
                                  }
                                },
                              );
                              if (isSupported) availableChains.push(chain);
                            }

                            const hasMultipleChains =
                              availableChains.length > 1;

                            return (
                              <button
                                key={group.symbol}
                                type="button"
                                data-token-index={index}
                                onClick={() => handleTokenSelect(group.symbol)}
                                className="w-full flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-[--color-alert-background] border border-transparent transition-all text-left"
                              >
                                <img
                                  src={firstCurrency.logoUrl}
                                  alt={group.symbol}
                                  className="w-6 h-6 flex-shrink-0 rounded-full"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="font-semibold text-sm text-[--color-text-primary]">
                                    {group.symbol}
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {hasMultipleChains ? (
                                    <ChevronRight className="w-4 h-4 text-[--color-text-secondary]" />
                                  ) : (
                                    <>
                                      {availableChains
                                        .slice(0, 3)
                                        .map((chain, idx) => (
                                          <img
                                            key={idx}
                                            src={chain.logoUrl}
                                            alt={chain.name}
                                            className="w-4 h-4 rounded-full"
                                            title={chain.name}
                                          />
                                        ))}
                                      {availableChains.length > 3 && (
                                        <span className="text-xs text-[--color-text-secondary] ml-0.5">
                                          +{availableChains.length - 3}
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    </motion.div>
                  ) : (
                    /* Chain List View */
                    <motion.div
                      key="chains"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 12 }}
                      transition={{
                        type: "tween",
                        ease: [0.25, 0.46, 0.45, 0.94],
                        duration: 0.12,
                      }}
                      className="p-2 space-y-3"
                    >
                      <div>
                        <div className="px-2 pb-2 text-xs font-semibold text-[--color-text-secondary] tracking-wide">
                          {searchQuery
                            ? "Results"
                            : `Select chain for ${selectedTokenSymbol}`}
                        </div>
                        {(() => {
                          const selectedGroup = groupedCurrencies.find(
                            (g) => g.symbol === selectedTokenSymbol,
                          );
                          if (!selectedGroup) return null;

                          const supportedChains: Array<{
                            chain: Chain;
                            currency: Currency;
                          }> = [];

                          for (const chain of chains) {
                            const supportedCurrency =
                              selectedGroup.currencies.find((currency) => {
                                try {
                                  const caip19 = currency.on(chain);
                                  return (
                                    caip19 !== null && caip19 !== undefined
                                  );
                                } catch {
                                  return false;
                                }
                              });
                            if (supportedCurrency) {
                              supportedChains.push({
                                chain,
                                currency: supportedCurrency,
                              });
                            }
                          }

                          const filtered = searchQuery.trim()
                            ? supportedChains.filter(({ chain }) =>
                                chain.name
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase()),
                              )
                            : supportedChains;

                          if (filtered.length === 0) {
                            return (
                              <div className="px-2 py-4 text-sm text-center text-[--color-text-secondary]">
                                No chains match "{searchQuery}"
                              </div>
                            );
                          }

                          return filtered.map(({ chain, currency }) => {
                            const isSelected = selectedChainId === chain.id;
                            return (
                              <button
                                key={chain.id}
                                type="button"
                                data-chain-id={chain.id}
                                onClick={() =>
                                  handleChainSelect(currency.id, chain.id)
                                }
                                className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-lg transition-all text-left ${
                                  isSelected
                                    ? "bg-[--color-alert-background] border border-[--color-modal-border]"
                                    : "border border-transparent hover:bg-[--color-alert-background]"
                                }`}
                              >
                                <img
                                  src={chain.logoUrl}
                                  alt={chain.name}
                                  className="w-6 h-6 flex-shrink-0 rounded-full"
                                />
                                <span className="font-semibold text-sm flex-1 text-[--color-text-primary]">
                                  {chain.name}
                                </span>
                                {isSelected && (
                                  <Check className="w-5 h-5 text-[--color-text-primary] flex-shrink-0" />
                                )}
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
